// ============================================================================
// JOURNAL ENTRY SERVICE — posting engine consumer (ADR-0015)
// Entity + Business Unit scoped via X-Entity-ID / X-Business-Unit-ID headers.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { journalEntryEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  TransactionDto,
  CreateJournalEntryRequest,
  UpdateJournalEntryRequest,
  JournalEntryQueryParams,
  OutstandingOpenItemDto,
  LedgerBalanceDto,
} from '../types';

class JournalEntryService {
  /** Paged voucher register. */
  async getAll(params?: JournalEntryQueryParams): Promise<PaginatedResponse<TransactionDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(journalEntryEndpoints.list(), params)
      : journalEntryEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<TransactionDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<TransactionDto> {
    const response = await get<ApiResponse<TransactionDto>>(journalEntryEndpoints.getById(id));
    return response.data.data;
  }

  /** Post a manual journal entry. Returns the posted transaction (header + lines). */
  async create(data: CreateJournalEntryRequest): Promise<TransactionDto> {
    const response = await post<ApiResponse<TransactionDto>>(journalEntryEndpoints.create(), data);
    return response.data.data;
  }

  /** Edit an unlocked journal entry in place (ADR-0016). */
  async update(id: ID, data: UpdateJournalEntryRequest): Promise<TransactionDto> {
    const response = await put<ApiResponse<TransactionDto>>(journalEntryEndpoints.update(id), data);
    return response.data.data;
  }

  /** Soft-delete an unlocked journal entry; rowVersion guards concurrency. */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(journalEntryEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  /** Outstanding bills on an open-item ledger (optionally a party / required side) for Against-Ref. */
  async getOutstandingItems(ledgerId: ID, partyId?: ID, side?: string): Promise<OutstandingOpenItemDto[]> {
    const url = buildEndpointWithQuery(journalEntryEndpoints.outstandingItems(), { ledgerId, partyId, side });
    const response = await get<ApiResponse<OutstandingOpenItemDto[]>>(url);
    return response.data.data ?? [];
  }

  /** Scoped ledger balance (BU-wise / consolidated by context) as of a date.
   *  excludeTransactionId omits the voucher being edited so its amount isn't double-counted. */
  async getLedgerBalance(ledgerId: ID, asOf?: string, excludeTransactionId?: ID): Promise<LedgerBalanceDto> {
    const url = buildEndpointWithQuery(journalEntryEndpoints.ledgerBalance(), { ledgerId, asOf, excludeTransactionId });
    const response = await get<ApiResponse<LedgerBalanceDto>>(url);
    return response.data.data;
  }
}

export const journalEntryService = new JournalEntryService();
export default journalEntryService;
